import React, {useState} from 'react';
import {useToast} from "@chakra-ui/react";
import {Drawer, Form} from "antd";
import {Input, Button} from "@nextui-org/react";
import Firebase from "../../Firebase/Firebase";
import {getFunctions, httpsCallable} from "firebase/functions";
import {ROLES, AdminRole, ROLE_META, ROLE_PERMISSIONS} from "../../Pages/Profile";

type Props = {
    openDrawer: boolean;
    closeDrawer: (val: boolean) => void;
};

const grantAdminAccess = httpsCallable(getFunctions(Firebase), 'grantAdminAccess');

const AddAdminUser: React.FC<Props> = ({openDrawer, closeDrawer}) => {
    const toast  = useToast();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    const handleAdd = async (values: any) => {
        setSaving(true);
        const fullName = `${values.firstname} ${values.surname}`.trim();
        const role: AdminRole = values.adminRole ?? 'support';

        try {
            const result: any = await grantAdminAccess({
                email:       values.email,
                fullName,
                adminRole:   role,
                permissions: ROLE_PERMISSIONS[role],
            });

            const { isNewUser, tempPassword } = result.data ?? {};

            toast({
                position: 'top',
                title: `${fullName} granted ${ROLE_META[role].label} access`,
                description: isNewUser
                    ? `New account created. Temporary password: ${tempPassword} — share it securely.`
                    : `Existing account upgraded to admin. They can log in with their current password.`,
                status: 'success',
                duration: 10000,
                isClosable: true,
            });

            form.resetFields();
            closeDrawer(false);
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:4000, isClosable:true});
        } finally {
            setSaving(false);
        }
    };

    return (
        <Drawer
            open={openDrawer}
            onClose={() => closeDrawer(false)}
            width={420}
            title="Add Admin User"
            bodyStyle={{padding:'16px 20px', background:'#f7f8fc'}}
            footer={
                <div style={{display:'flex', justifyContent:'flex-end', gap:10}}>
                    <Button size="sm" flat color="default" onPress={() => closeDrawer(false)}>
                        Cancel
                    </Button>
                    <Button size="sm" color="primary" disabled={saving} onPress={() => form.submit()}>
                        {saving ? 'Adding…' : 'Grant Access'}
                    </Button>
                </div>
            }
        >
            <Form form={form} layout="vertical" onFinish={handleAdd}>
                <div style={{background:'#fff', borderRadius:10, border:'1px solid #eee',
                             padding:'14px 16px', marginBottom:14}}>
                    <div style={{fontWeight:600, fontSize:12, color:'#555',
                                 letterSpacing:.4, marginBottom:12}}>PERSONAL INFO</div>

                    <Form.Item name="firstname" label="First name"
                               rules={[{required:true, message:'Required'}]}>
                        <Input placeholder="First name" width="100%" onChange={() => {}} />
                    </Form.Item>
                    <Form.Item name="surname" label="Last name"
                               rules={[{required:true, message:'Required'}]}>
                        <Input placeholder="Last name" width="100%" onChange={() => {}} />
                    </Form.Item>
                    <Form.Item name="email" label="Email address"
                               rules={[
                                   {required:true, message:'Required'},
                                   {type:'email', message:'Invalid email'},
                               ]}>
                        <Input type="email" placeholder="admin@example.com" width="100%" onChange={() => {}} />
                    </Form.Item>
                </div>

                <div style={{background:'#fff', borderRadius:10, border:'1px solid #eee',
                             padding:'14px 16px', marginBottom:14}}>
                    <div style={{fontWeight:600, fontSize:12, color:'#555',
                                 letterSpacing:.4, marginBottom:12}}>ROLE &amp; PERMISSIONS</div>

                    <Form.Item name="adminRole" label="Role" initialValue="support"
                               rules={[{required:true}]}>
                        <select style={{width:'100%', padding:'8px 12px', borderRadius:8,
                                        border:'1px solid #eee', fontSize:13, color:'#333'}}>
                            {ROLES.map(r => (
                                <option key={r} value={r}>
                                    {ROLE_META[r].label} — {ROLE_META[r].description}
                                </option>
                            ))}
                        </select>
                    </Form.Item>

                    <div style={{background:'#f8fafc', borderRadius:8, padding:'10px 12px',
                                 fontSize:12, color:'#555', marginTop:4}}>
                        <b>New account:</b> a temporary password will be shown in the success message.<br/>
                        <b>Existing account</b> (buyer/seller): admin access is added to their current account —
                        no password change needed.
                    </div>
                </div>
            </Form>
        </Drawer>
    );
};

export default AddAdminUser;
